import { createSlice } from '@reduxjs/toolkit';

import SDK from '../Components/SDK';
import { clearState } from './clearSlice';

export const LINK_PREVIEW_STATUS = {
    LOADING: 'loading',
    READY: 'ready',
    // No SEO title, or no such page: no card for the whole session.
    EMPTY: 'empty',
    // Not fetched right now: no card until the entry expires and is asked again.
    FAILED: 'failed',
};

const RETRY_AFTER_MS = 60 * 1000;
const HTTP_OK = 200;
const HTTP_BAD_REQUEST = 400;

// Previews are shared by the composer and the message bubbles; dismissed links are per chat.
const initialState = {
    byUrl: {},
    dismissedByChat: {},
};

const linkPreviewSlice = createSlice({
    name: 'linkPreview',
    initialState,
    reducers: {
        setLinkPreview(state, action) {
            const { url, status, data = null } = action.payload;
            state.byUrl[url] = { status, data };
        },
        dismissLinkPreview(state, action) {
            const { chatId, url } = action.payload;
            const dismissed = state.dismissedByChat[chatId] || [];
            if (!dismissed.includes(url)) state.dismissedByChat[chatId] = [...dismissed, url];
        },
        resetDismissedLinkPreviews(state, action) {
            delete state.dismissedByChat[action.payload];
        },
        forgetLinkPreview(state, action) {
            delete state.byUrl[action.payload];
        },
    },
    extraReducers: builder => {
        builder.addCase(clearState, () => initialState);
    },
});

export const { setLinkPreview, dismissLinkPreview, resetDismissedLinkPreviews, forgetLinkPreview } =
    linkPreviewSlice.actions;

export const selectLinkPreview = (state, url) => state.linkPreview.byUrl[url];

export const selectDismissedLinks = (state, chatId) => state.linkPreview.dismissedByChat[chatId];

/**
 * Loads the preview of a link once per session; later calls reuse the stored result.
 *
 * @param {string} url Absolute http(s) URL.
 */
export const loadLinkPreview = url => async (dispatch, getState) => {
    if (!url || selectLinkPreview(getState(), url)) return;
    dispatch(setLinkPreview({ url, status: LINK_PREVIEW_STATUS.LOADING }));
    const response = await SDK.getLinkPreview(url).catch(() => null);
    const status = toStatus(response);
    dispatch(setLinkPreview({ url, status, data: status === LINK_PREVIEW_STATUS.READY ? response.data : null }));
    if (status === LINK_PREVIEW_STATUS.FAILED) {
        setTimeout(() => dispatch(forgetLinkPreview(url)), RETRY_AFTER_MS);
    }
};

// Only answers about the page are final; network errors, 429 and 503 may pass.
const toStatus = response => {
    if (response?.statusCode === HTTP_OK) {
        return response.data?.title ? LINK_PREVIEW_STATUS.READY : LINK_PREVIEW_STATUS.EMPTY;
    }
    return response?.statusCode === HTTP_BAD_REQUEST ? LINK_PREVIEW_STATUS.EMPTY : LINK_PREVIEW_STATUS.FAILED;
};

export default linkPreviewSlice.reducer;
